import type AutocompleteElement from '@github/auto-complete-element'
import type SeveritySelectionElement from '../../../../components/advisories/severity-selection-element'
import type SeveritySelectionNextElement from '../../../../components/advisories/severity-selection-next-element'
import type {SimpleResponse} from '@github-ui/remote-form'
import {handleCommentCancelButtonClick} from '../behaviors/commenting/edit'
import {hideFormGroupError} from './edit'
import {on} from 'delegated-events'
import {remoteForm} from '@github-ui/remote-form'
import {requestSubmit} from '@github-ui/form-utils'

on(
  'details-menu-selected',
  '.js-advisory-menu',
  function (event) {
    const menuItem = event.detail.relatedTarget
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const classToShow = menuItem.getAttribute('data-advisory-info-show')!
    const elementsToToggle = document.querySelectorAll<HTMLElement>(
      '.js-show-if-publishing, .js-show-if-requesting-cve',
    )

    for (const element of elementsToToggle) {
      element.hidden = !element.classList.contains(classToShow)
    }
  },
  {capture: true},
)

function toggleListVisibility(list: HTMLElement) {
  const credits = list.querySelectorAll<HTMLElement>('.js-advisory-credit-row')
  const listIsEmpty = Array.from(credits).every(credit => credit.hidden)
  list.hidden = listIsEmpty
}

function addCredit(container: HTMLElement, response: SimpleResponse) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const credit = response.html.querySelector<HTMLElement>('.js-advisory-credit-row')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const recipientId = credit.getAttribute('data-recipient-id')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const list = container.querySelector<HTMLElement>('.js-advisory-credits-list')!
  const existingCredit = list.querySelector<HTMLElement>(`.js-advisory-credit-row[data-recipient-id='${recipientId}']`)

  if (existingCredit) {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const destroyField = existingCredit.querySelector<HTMLInputElement>('.js-advisory-credit-destroy-field')!

    if (!existingCredit.hidden) {
      existingCredit.classList.add('user-already-added')
    }

    destroyField.value = 'false'
    existingCredit.hidden = false
  } else {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const nextIndexField = container.querySelector<HTMLInputElement>('.js-next-credit-index-field')!

    list.append(credit)
    nextIndexField.value = String(Number(nextIndexField.value) + 1)
  }

  toggleListVisibility(list)
}

function removeCredit(credit: HTMLElement) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const destroyField = credit.querySelector<HTMLInputElement>('.js-advisory-credit-destroy-field')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const list = credit.closest<HTMLElement>('.js-advisory-credits-list')!

  destroyField.value = 'true'
  credit.hidden = true
  credit.classList.remove('user-already-added')

  toggleListVisibility(list)
}

remoteForm('.js-repository-advisory-add-credit-form', async function (form, wants) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const container = form.closest<HTMLElement>('.js-repository-advisory-form-container')!

  form.reset()

  const response = await wants.html()
  if (!response) return

  addCredit(container, response)
})

on('auto-complete-change', '.js-add-credit-auto-complete', function (event) {
  const autoComplete = event.target as AutocompleteElement
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const container = autoComplete.closest<HTMLElement>('.js-repository-advisory-form-container')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const form = container.querySelector<HTMLFormElement>('.js-repository-advisory-add-credit-form')!

  requestSubmit(form)
})

on('click', '.js-advisory-credit-remove-button', function (event) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const credit = event.currentTarget.closest<HTMLElement>('.js-advisory-credit-row')!

  removeCredit(credit)
})

on('click', '.js-repository-advisory-credit-popover-edit-button', function (event) {
  const button: HTMLButtonElement = event.target as HTMLButtonElement
  if (!button) return
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const form = button.closest<HTMLFormElement>('form')!
  requestSubmit(form)
})

function toggleRepositoryAdvisoryEditForm(shown: boolean) {
  const detailElements = document.querySelectorAll<HTMLElement>('.js-repository-advisory-details')
  for (const detailElement of detailElements) {
    detailElement.hidden = shown
  }

  const editFormContainer = document.querySelector<HTMLElement>('.js-repository-advisory-edit-form-container')
  if (editFormContainer) {
    editFormContainer.hidden = !shown
  }
}

on('click', '.js-repository-advisory-edit-button', function () {
  toggleRepositoryAdvisoryEditForm(true)
})

on('change', '.js-bypass-branch-protections', function ({currentTarget}) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const mergeMessageContainer = currentTarget.closest<HTMLElement>('.merge-message')!
  const newStateValue = (currentTarget as HTMLInputElement).checked
  const mergeButtons = mergeMessageContainer.querySelectorAll<HTMLButtonElement>('.merge-button')

  for (const button of mergeButtons) {
    button.disabled = !newStateValue
  }
})

on('click', '.js-repository-advisory-edit-form-cancel-button-next', function (event) {
  // Check whether or not the user wants to give up editing.
  // Form values revert to their previous default if they agree.
  if (!handleCommentCancelButtonClick(event)) {
    return
  }

  const severitySelectionNextElement = document.querySelector<SeveritySelectionNextElement>('severity-selection-next')

  if (severitySelectionNextElement) {
    // The "Severity" drop-down must be manually reverted back to its original value
    severitySelectionNextElement.resetSeveritySelectElement()

    // Re-select the original calculator controls and remove any lingering errors.  This is necessary because the
    // reversal of form values above is programmatic and does not trigger change events.
    // TODO:  Do we still to call `handleVectorStringInput()` and `handleVectorStringBlur()`?
    severitySelectionNextElement.handleVectorStringInput()
    severitySelectionNextElement.handleVectorStringBlur()
    severitySelectionNextElement.toggleSeverityCalculator(severitySelectionNextElement.severitySelect.value)
  }

  // Actually close the form
  toggleRepositoryAdvisoryEditForm(false)
})

on('change', '.js-cve-selection', function (event) {
  const selection = event.currentTarget as HTMLSelectElement

  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const existingCve = selection.form!.querySelector<HTMLElement>('.js-existing-cve')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const requestingCve = selection.form!.querySelector<HTMLElement>('.js-requesting-cve')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const notApplicableCve = selection.form!.querySelector<HTMLElement>('.js-not-applicable-cve')!

  existingCve.hidden = selection.value !== 'existing'
  requestingCve.hidden = selection.value !== 'requesting'
  notApplicableCve.hidden = selection.value !== 'not_applicable'
})
on('focusout', '.js-existing-cve', hideFormGroupError)

// Repository advisory confidential comment composer:
// When the "Confidential" checkbox is toggled, update the comment field
// placeholder and gate the close/reopen submit buttons (a confidential comment
// must not also close or reopen the advisory).
function syncConfidentialComposerState(checkbox: HTMLInputElement) {
  const form = checkbox.closest<HTMLFormElement>('form')
  if (!form) return

  const confidential = checkbox.checked

  const commentField = form.querySelector<HTMLTextAreaElement>('.js-comment-field')
  if (commentField) {
    if (confidential) {
      const controls = checkbox.closest<HTMLElement>('.advisory-confidential-controls-check')
      const confidentialPlaceholder = controls?.getAttribute('data-confidential-placeholder')
      if (confidentialPlaceholder != null) {
        if (commentField.getAttribute('data-default-placeholder') == null) {
          commentField.setAttribute('data-default-placeholder', commentField.placeholder)
        }
        commentField.placeholder = confidentialPlaceholder
      }
    } else {
      const defaultPlaceholder = commentField.getAttribute('data-default-placeholder')
      if (defaultPlaceholder != null) {
        commentField.placeholder = defaultPlaceholder
      }
    }
  }

  for (const button of form.querySelectorAll<HTMLButtonElement>('.js-comment-and-button')) {
    button.disabled = confidential

    const explanation = button.getAttribute('data-confidential-disabled-tooltip')
    if (confidential && explanation != null) {
      // The `.tooltipped` CSS renders `attr(aria-label)`, but aria-label also
      // overrides the accessible name — so keep the visible action text in it
      // (WCAG 2.5.3 Label in Name) followed by the disabled-state explanation.
      const label = button.querySelector('.js-form-action-text')?.textContent?.trim()
      button.classList.add('tooltipped', 'tooltipped-s', 'tooltipped-multiline')
      button.setAttribute('aria-label', label ? `${label}. ${explanation}` : explanation)
    } else {
      button.classList.remove('tooltipped', 'tooltipped-s', 'tooltipped-multiline')
      button.removeAttribute('aria-label')
    }
  }
}

on('change', '.advisory-confidential-checkbox', function (event) {
  syncConfidentialComposerState(event.currentTarget as HTMLInputElement)
})

// A successful comment submission calls `form.reset()` (see
// behaviors/commenting/ajax.ts), which restores the checkbox to its default
// without firing `change`. Re-sync after the native reset has applied so the
// placeholder and disabled close/reopen buttons don't stay in confidential mode.
on('reset', '.js-advisory-comment-form', function (event) {
  const form = event.currentTarget as HTMLFormElement
  setTimeout(() => {
    const checkbox = form.querySelector<HTMLInputElement>('.advisory-confidential-checkbox')
    if (checkbox) syncConfidentialComposerState(checkbox)
  })
})
